import React from 'react';
import { usePermissions } from '../../hooks/usePermissions';

interface User {
  name: string;
  email: string;
  account: any;
}

interface PermissionGuardProps {
  user: User | null;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireModule?: 'teamSettings' | 'contentReview' | 'specWriter' | 'strategy';
  requireSubcategory?:
    | 'canManageUsers'
    | 'canViewUsers'
    | 'canReviewContent'
    | 'canMakeReviewDecisions'
    | 'canUseSpecWriter';
  requireAdmin?: boolean;
  silent?: boolean; // When true, just returns null instead of showing permission message
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({
  user,
  children,
  fallback = null,
  requireModule,
  requireSubcategory,
  requireAdmin = false,
  silent = false
}) => {
  const { permissions, loading, error, hasModuleAccess, hasSubcategoryAccess, isAdmin } = usePermissions(user);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      }}>
        <div>Loading permissions...</div>
      </div>
    );
  }

  if (!permissions || !user) {
    if (silent) return null;

    // Check if this is a "user not found" error (unregistered user)
    const isUserNotFound = error?.includes('not found') || error?.includes('hasn\'t been set up');

    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        backgroundColor: isUserNotFound ? '#fff3cd' : '#f8d7da',
        border: `1px solid ${isUserNotFound ? '#ffeaa7' : '#f5c6cb'}`,
        borderRadius: '8px',
        color: isUserNotFound ? '#856404' : '#721c24',
        maxWidth: '500px',
        margin: '40px auto'
      }}>
        {isUserNotFound ? (
          <>
            <h3 style={{ marginBottom: '15px' }}>Account Not Set Up</h3>
            <p>Your account hasn't been set up yet.</p>
            <p style={{ marginTop: '10px' }}>Please contact <strong>Fredrik Helander</strong> to get access.</p>
          </>
        ) : (
          <p>Authentication required to access this feature.</p>
        )}
      </div>
    );
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin()) {
    if (silent) return null;
    
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '4px',
        color: '#721c24'
      }}>
        <p>Administrator access required for this feature.</p>
      </div>
    );
  }

  // Check module access requirement
  if (requireModule && !hasModuleAccess(requireModule)) {
    if (silent) return null;
    
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '4px',
        color: '#721c24'
      }}>
        <p>You don't have access to this module.</p>
        <p style={{ fontSize: '14px', marginTop: '10px' }}>
          Contact an administrator to request access.
        </p>
      </div>
    );
  }

  // Check subcategory access requirement
  if (requireSubcategory && !hasSubcategoryAccess(requireSubcategory)) {
    if (silent) return null;
    
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '4px',
        color: '#721c24'
      }}>
        <p>You don't have permission to access this feature.</p>
        <p style={{ fontSize: '14px', marginTop: '10px' }}>
          Contact an administrator to request access.
        </p>
      </div>
    );
  }

  // If all checks pass, render children
  return <>{children}</>;
};

export default PermissionGuard;